/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-07-24
 */

// Add third-party dependencies.
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Add local dependencies.
import {
  useLazySessionStatusQuery,
  useLazyGetCsrfTokenQuery,
  setCredentials,
  updateCsrfToken,
  logout,
  setInitialized,
  incrementSessionCheckAttempts,
  resetSessionCheckAttempts,
  selectIsAuthenticated,
  selectCsrfToken,
  selectIsInitialized,
  selectSessionCheckAttempts,
} from '../features/auth';
import { isSessionExpired } from '../utils/sessionAuth';
import type { RootState } from '../store';

/**
 * Custom hook for managing session-based authentication.
 *
 * Features:
 * - Automatically checks session status on mount with retry logic
 * - Fetches CSRF tokens when needed
 * - Handles session expiration
 * - Provides authentication state and utilities
 * - Robust error handling and recovery
 *
 * @returns Authentication state and utilities
 * @example
 * const { isAuthenticated, isLoading, checkSession, getCsrfToken } = useSessionAuth();
 */
export const useSessionAuth = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const csrfToken = useSelector(selectCsrfToken);
  const isInitialized = useSelector(selectIsInitialized);
  const sessionCheckAttempts = useSelector(selectSessionCheckAttempts);

  const [triggerSessionStatus, { isLoading: isCheckingSession }] =
    useLazySessionStatusQuery();
  const [triggerGetCsrfToken, { isLoading: isFetchingCsrf }] =
    useLazyGetCsrfTokenQuery();

  // Maximum retry attempts for session checks
  const MAX_SESSION_CHECK_ATTEMPTS = 3;
  const RETRY_DELAY = 1000; // 1 second

  /**
   * Check current session status and update authentication state with retry logic.
   * This is called automatically on mount and can be called manually.
   */
  const checkSession = async (isRetry = false) => {
    try {
      // If this is a retry, increment attempt counter
      if (isRetry) {
        dispatch(incrementSessionCheckAttempts());
      }

      const result = await triggerSessionStatus().unwrap();

      if (result.authenticated && result.user) {
        // User is authenticated, update state and reset attempts
        dispatch(
          setCredentials({
            user: result.user,
            csrfToken: result.csrf_token,
          })
        );
        dispatch(resetSessionCheckAttempts());
      } else {
        // User is not authenticated, clear state
        dispatch(logout());
      }
    } catch (error) {
      console.warn('Session check failed:', error);

      // If we haven't exceeded max attempts, retry after delay
      if (sessionCheckAttempts < MAX_SESSION_CHECK_ATTEMPTS) {
        console.log(
          `Retrying session check (attempt ${sessionCheckAttempts + 1}/${MAX_SESSION_CHECK_ATTEMPTS})`
        );
        setTimeout(
          () => {
            checkSession(true);
          },
          RETRY_DELAY * (sessionCheckAttempts + 1)
        ); // Exponential backoff
      } else {
        // Max attempts reached, assume not authenticated
        console.warn('Max session check attempts reached, logging out');
        dispatch(logout());
      }
    }
  };

  /**
   * Initialize session check on app startup.
   * This handles the initial session validation with proper state management.
   */
  const initializeSession = async () => {
    if (isInitialized) {
      return; // Already initialized
    }

    // If we have persisted auth state, validate it with the server
    if (isAuthenticated) {
      await checkSession();
    } else {
      // No persisted state, mark as initialized
      dispatch(setInitialized(true));
    }
  };

  /**
   * Fetch a new CSRF token and update the store.
   * This is useful when making state-changing requests.
   */
  const fetchCsrfToken = async () => {
    try {
      const result = await triggerGetCsrfToken().unwrap();
      dispatch(updateCsrfToken(result.csrf_token));
      return result.csrf_token;
    } catch (error) {
      console.error('Failed to fetch CSRF token:', error);
      return null;
    }
  };

  /**
   * Check if the current session appears to be expired.
   * This is a client-side check based on stored expiry time.
   */
  const checkSessionExpiry = () => {
    if (isAuthenticated && isSessionExpired()) {
      // Session appears expired, check with server
      checkSession();
    }
  };

  /**
   * Logout the current user by clearing local state.
   * The actual session destruction should be handled by a logout API call.
   */
  const logoutUser = () => {
    dispatch(logout());
  };

  // Initialize session on mount
  useEffect(() => {
    initializeSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Periodically check for session expiry (every 5 minutes)
  useEffect(() => {
    if (isAuthenticated && isInitialized) {
      const interval = setInterval(checkSessionExpiry, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isInitialized]);

  return {
    // Authentication state
    isAuthenticated,
    csrfToken,
    isInitialized,

    // Loading states
    isLoading: isCheckingSession || isFetchingCsrf || !isInitialized,
    isCheckingSession,
    isFetchingCsrf,

    // Utilities
    checkSession,
    fetchCsrfToken,
    logoutUser,

    // Session management
    isSessionExpired: isSessionExpired(),
    sessionCheckAttempts,
  };
};

/**
 * Hook for components that require authentication.
 * Automatically redirects to login if not authenticated.
 *
 * @param redirectTo - Path to redirect to if not authenticated (default: '/login')
 * @returns Authentication state
 * @example
 * const { user, isLoading } = useRequireAuth();
 */
export const useRequireAuth = (redirectTo: string = '/login') => {
  const { isAuthenticated, isLoading } = useSessionAuth();
  const user = useSelector((state: RootState) => state.auth.user);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // Redirect to login page
      window.location.href = redirectTo;
    }
  }, [isAuthenticated, isLoading, redirectTo]);

  return {
    user,
    isAuthenticated,
    isLoading,
  };
};

export default useSessionAuth;
